import { store } from "../../../../../../stores/Store"
import { Button } from "@mui/material"
import React from "react"
import Ajax from '../../../../../../Ajax'
import { Alert } from '../../../../../..'
import { Icons } from '../../../../../../misc/Icons'
import { Theme } from '../../../../../../misc/Theme'
import { formatDistance } from "date-fns"
import { UTCDate } from "@date-fns/utc"
import WorkflowEvidence from "./WorkflowEvidence"
import WorkflowSetup from "./WorkflowSetup"
import { Case, WorkflowStep, Workflow as IWorkflow } from "../../../../../../models/Interfaces"
import InlineMenu from "../../../../../../misc/InlineMenu"
import WorkflowCreateForm from "./WorkflowCreateForm"

const caseWorflowStepStyle = {
    width: "100%",
    display: "grid",
    gap: "10px",
    backgroundColor: "white",
    borderRadius: "5px",
    padding: "10px",
    boxShadow: "0 0 10px 0 #00000055",
    color: "#444444",
    marginBottom: "10px"
}


interface WorkflowProps {
    caseNode: Case;
}


export default function Workflow ({caseNode}: WorkflowProps) {

    const [expandedWorkflowId, setExpandedWorkflowId] = React.useState(null)
    const [workflow, setWorkflow] = React.useState<IWorkflow>(null)
    const [loading, setLoading] = React.useState(true)

    const disableForm = store.AppStore.checkJwtRoles(["client_account", "sponsor_account"], false)
    const steps = workflow?.steps?.sort((a, b) => a.orderInd - b.orderInd)
    
    React.useEffect(() => {
        loadWorkflow()
    }, [caseNode])

    const loadWorkflow = async () => {
        setLoading(true)
        await Ajax.Workflow.Get(caseNode.uid).then(response => {
            setWorkflow(response.data)
        }).catch(() => {
            setWorkflow(null)
        })
        setLoading(false)
    }

    const reactivateStep = async (nextStep: WorkflowStep) => {
        setLoading(true)
        
        try {

            var nextStepPromise = Ajax.Workflow.StepUpdate({...nextStep, active: true, completedAt: null})
            
            var currentStep = steps.find(step => step.active)
            if (currentStep) {
                await Ajax.Workflow.StepUpdate({...currentStep, active: false})
            }

            await nextStepPromise
        
        } catch (ex) {
            Alert({message: "Failed to update workflow"})
        }

        await loadWorkflow().then(() => setExpandedWorkflowId(null))
    }   

    const completeStep = async (currentStep: WorkflowStep) => {
        setLoading(true)
        
        try {

            var currentStepPromise = Ajax.Workflow.StepUpdate({...currentStep, active: false, completedAt: new Date().toISOString()})
            
            var nextStep = steps.find(step => step.orderInd > currentStep.orderInd && !step.completedAt)
            if (nextStep) {
                await Ajax.Workflow.StepUpdate({...nextStep, active: true})
            }

            await currentStepPromise
        
        } catch (ex) {
            Alert({message: "Failed to update workflow"})
        }

        await loadWorkflow().then(() => setExpandedWorkflowId(null))
    }   

    const deleteWorkflow = async () => {
        setLoading(true)

        await Ajax.Workflow.Delete(caseNode.uid).then(async () => {
            await loadWorkflow()
        }).catch(() => {
            Alert({message: "Failed to delete workflow"})
        })
    }

    if (!workflow && !loading) {
        return <WorkflowCreateForm caseNode={caseNode} />
    }

    if (!workflow && loading) {
        return <></>
    }

    return (
        <div style={{width: "95%", maxWidth: "500px", height: "fit-content", display: "grid", gap: "5px"}}>
            <div style={{display: "flex", gap: "10px", alignItems: "center", color: "#565656"}}>
                <small>{workflow.label}</small>
                
                <InlineMenu 
                    opener={<Icons.More style={{fontSize: "16px"}} />}
                    menuItems={[
                        {label: "Remove Workflow from this Case", action: () => deleteWorkflow()}
                    ]} />
            </div>
            
            {steps?.map((workflowStep: WorkflowStep, i) => {
                return (
                    <div style={caseWorflowStepStyle} key={i}>
                        <div>
                            <div style={{display: "flex", gap: "10px", alignItems: "center"}}>
                                <div style={{display: "flex", gap: "0px", alignItems: "center"}}>
                                    <label style={{fontWeight: workflowStep.active ? 600 : 300}} onClick={() => setExpandedWorkflowId(workflowStep.uid)}>
                                        {workflowStep.label}
                                    </label>

                                    {!workflowStep.active ? (
                                        <Button 
                                            color="info"
                                            className="btn btn-icon btn-sm"
                                            onClick={() => setExpandedWorkflowId(workflowStep.uid !== expandedWorkflowId ? workflowStep.uid : null)}>
                                            <Icons.More style={{fontSize: "20px"}} />
                                        </Button>
                                    ) : null}
                                </div>

                                {workflowStep.completedAt ? (
                                    <small style={{fontWeight: 300, display: "flex", alignItems: "center", gap: "5px", marginLeft: "auto"}}>
                                        Completed {formatDistance(new UTCDate(workflowStep.completedAt), new Date())} ago
                                        <Icons.Tick style={{fontSize: "16px", color: Theme.palette.success.main}} />
                                    </small>
                                ) : null}

                                {!workflowStep.completedAt && workflowStep.targetDate ? (
                                    <small style={{fontWeight: 300, display: "flex", alignItems: "center", gap: "5px", marginLeft: "auto"}}>
                                        Due in {formatDistance(new UTCDate(workflowStep.targetDate), new UTCDate(), { addSuffix: true })}
                                        <Icons.Timer style={{fontSize: "16px", color: Theme.palette.warning.main}} />
                                    </small>
                                ) : null}
                            </div>
                        </div>

                        {workflowStep.active || workflowStep.uid === expandedWorkflowId ? (
                            <>
                                <small>{workflowStep.description}</small>

                                {workflowStep.additionalContentName === "SET_UP" ? (
                                    <WorkflowSetup caseNode={caseNode} />
                                ) : null}

                                {workflowStep.additionalContentName === "EVIDENCE" ? (
                                    <WorkflowEvidence caseNode={caseNode} workflowStep={workflowStep} />
                                ) : null}

                                {!workflowStep.active && workflowStep.completedAt ? (
                                    <Button
                                        disabled={loading || disableForm}
                                        className="btn btn-sm"
                                        variant="contained"
                                        color="info"
                                        onClick={() => reactivateStep(workflowStep)}>
                                        Redo this step
                                    </Button>
                                ) : null}
                            </>
                        ) : null}

                        {workflowStep.active ? (
                            <Button
                                disabled={loading || disableForm}
                                className="btn btn-sm"
                                variant="contained"
                                color="success"
                                onClick={() => completeStep(workflowStep)}>
                                <Icons.Tick /> Mark as complete
                            </Button>
                        ) : null}
                    </div>
                )
            })}
        </div>
    )
}